import React, { useState, useEffect, useContext } from 'react';
import { supabase } from './supabaseClient';
import { AuthContext } from './AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import ComparePlansTable from './components/ComparePlansTable'; // Import the new component
import StripePricingTable from './components/StripePricingTable';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';

const PricingPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stripe config for Live Agent pricing table
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  const liveAgentPricingTableId = process.env.REACT_APP_STRIPE_LIVE_AGENT_PRICING_TABLE_ID || '';

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('is_active', true)
        .order('price_usd');

      if (error) {
        setError(error.message);
      } else {
        setPackages(data);
      }
      setLoading(false);
    };

    fetchPackages();
  }, []);

  if (loading) {
    return <Container sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Typography variant="h2" align="center" color="text.primary" gutterBottom>
        Pricing
      </Typography>
      <Typography variant="h5" align="center" color="text.secondary" paragraph>
        Choose a plan that works for you. Start for free.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Stripe Pricing Table (shown for logged-in users to preserve session/context) */}
      {currentUser ? (
        <>
          {/* AI Automation Packages */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3 }}>
              AI Automation Packages
            </Typography>
            <StripePricingTable currentUser={currentUser} />
          </Box>

          {/* Live Human Agent Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1 }}>
              Live Human Agent Support
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Add live agents to handle complex queries. $19/seat/month.
            </Typography>
            {!liveAgentPricingTableId || !publishableKey ? (
              <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
                Live Agent pricing table is not configured. Please set REACT_APP_STRIPE_LIVE_AGENT_PRICING_TABLE_ID.
              </Alert>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: `<stripe-pricing-table
                    pricing-table-id="${liveAgentPricingTableId}"
                    publishable-key="${publishableKey}"
                    customer-email="${currentUser?.email || ''}"
                  ></stripe-pricing-table>`
                }}
              />
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ mt: 2, mb: 4, textAlign: 'center' }}>
          <Alert severity="info">Please sign in to subscribe. You can review plans below.</Alert>
        </Box>
      )}


      {!currentUser && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account? <Link to="/login">Sign in here</Link>
          </Typography>
        </Box>
      )}

      {/* Render the new ComparePlansTable component below everything else */}
      <ComparePlansTable packages={packages} />

    </Container>
  );
};

export default PricingPage; 